import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, Loader2, HardDriveDownload, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-[8px] border border-line bg-white p-3.5" data-testid={testId}>
    <p className="text-[11.5px] uppercase tracking-[0.04em] text-ink-tertiary">{label}</p>
    <p className="mt-1 text-[19px] font-semibold text-ink tabular-nums">{value}</p>
  </div>
);

/** Solo visible para owner: resumen del estado real de los datos y recarga completa del seed
 * operativo. La recarga corre en segundo plano (el proxy corta a los 120s y la regeneración
 * completa con evidencia y 12 meses de historial financiero tarda más que eso): el botón solo
 * dispara la tarea, y el progreso se sigue por polling contra /admin/reseed-status. */
export const DataTab = () => {
  const { t } = useT();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [word, setWord] = useState("");
  const prevStatusRef = useRef(undefined);

  const { data: s, isLoading } = useQuery({
    queryKey: ["admin-data-summary"],
    queryFn: async () => (await api.get("/admin/data-summary")).data,
  });

  const { data: statusData } = useQuery({
    queryKey: ["admin-reseed-status"],
    queryFn: async () => (await api.get("/admin/reseed-status")).data,
    refetchInterval: (query) => (query.state.data?.job?.status === "en_curso" ? 2500 : false),
  });
  const job = statusData?.job;
  const isRunning = job?.status === "en_curso";

  useEffect(() => {
    if (!job) return;
    const prev = prevStatusRef.current;
    if (prev === "en_curso" && job.status === "completado") {
      toast.success(t("settings.data.reload_success"));
      setConfirmOpen(false);
      setWord("");
      // Todo el dataset operativo cambió: invalidar solo el resumen dejaría el resto de la
      // app (órdenes, pagos, liquidaciones, torre de control, inicio) mostrando datos viejos
      // en caché hasta que venza su staleTime.
      qc.invalidateQueries();
    } else if (prev === "en_curso" && job.status === "fallido") {
      toast.error(job.error || t("settings.data.reload_failed"));
    }
    prevStatusRef.current = job.status;
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useMutation({
    mutationFn: () => api.post("/admin/reseed", { confirmacion: word }),
    onSuccess: () => {
      setConfirmOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-reseed-status"] });
    },
    onError: (e) => {
      if (e?.response?.status === 409) {
        setConfirmOpen(false);
        qc.invalidateQueries({ queryKey: ["admin-reseed-status"] });
        return;
      }
      toast.error(apiError(e, t("app.error")));
    },
  });

  const integrity = useMutation({
    mutationFn: async () => (await api.get("/admin/storage-integrity")).data,
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });
  const integrityResult = integrity.data;
  const downloadMissing = () => {
    const blob = new Blob([JSON.stringify(integrityResult.detalle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "archivos-faltantes.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="settings-data-tab">
      <section>
        <h2 className="mb-3 text-[13px] font-semibold text-ink">{t("settings.data.summary_title")}</h2>
        {isLoading ? (
          <p className="text-[13px] text-ink-tertiary">{t("app.loading")}</p>
        ) : s ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="data-summary-grid">
            <Stat testId="data-summary-ordenes-total" label={t("settings.data.orders_total")} value={s.ordenes.total} />
            <Stat testId="data-summary-vencidas" label={t("settings.data.overdue_pct")}
                 value={`${s.ordenes.vencidas_pct}% (${s.ordenes.vencidas}/${s.ordenes.abiertas})`} />
            <Stat testId="data-summary-alertas" label={t("settings.data.open_alerts")} value={s.torre_control.alertas_abiertas} />
            <Stat testId="data-summary-sin-facturar" label={t("settings.data.unbilled")}
                 value={`${formatMoney(s.facturacion.cerrado_sin_facturar_cents)}${s.facturacion.sin_facturar_pct != null ? ` (${s.facturacion.sin_facturar_pct}%)` : ""}`} />
            <Stat testId="data-summary-pagos-total" label={t("settings.data.payments_total")} value={s.pagos.total} />
            <Stat testId="data-summary-pagos-rango" label={t("settings.data.payments_range")}
                 value={s.pagos.fecha_min ? `${s.pagos.fecha_min.slice(0, 10)} → ${s.pagos.fecha_max.slice(0, 10)}` : "—"} />
            <Stat testId="data-summary-pagos-meses" label={t("settings.data.payments_months")} value={s.pagos.meses_distintos} />
            <Stat testId="data-summary-facturacion-mes" label={t("settings.data.month_billing")} value={formatMoney(s.facturacion.facturacion_mes_cents)} />
          </div>
        ) : null}
      </section>

      <section className="rounded-[8px] border border-line bg-white p-4" data-testid="storage-integrity-section">
        <h2 className="text-[13px] font-semibold text-ink">{t("settings.data.integrity_title")}</h2>
        <p className="mt-1 text-[12.5px] text-ink-secondary">{t("settings.data.integrity_desc")}</p>
        <div className="mt-3 flex items-center gap-3">
          <Button data-testid="storage-integrity-check-button" size="sm" variant="outline"
            disabled={integrity.isPending} onClick={() => integrity.mutate()}
            className="h-9 rounded-[6px] text-[13px]">
            {integrity.isPending ? (
              <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> {t("settings.data.integrity_checking")}</>
            ) : (
              <><HardDriveDownload className="mr-1.5 h-3.5 w-3.5" /> {t("settings.data.integrity_button")}</>
            )}
          </Button>
          {integrityResult && (
            <span data-testid="storage-integrity-result"
              className={integrityResult.faltan > 0 ? "text-[12.5px] text-state-critical" : "text-[12.5px] text-state-ok"}>
              {integrityResult.faltan > 0
                ? t("settings.data.integrity_missing", { faltan: integrityResult.faltan, revisados: integrityResult.revisados })
                : t("settings.data.integrity_ok", { revisados: integrityResult.revisados })}
            </span>
          )}
          {integrityResult?.faltan > 0 && (
            <Button data-testid="storage-integrity-download-button" size="sm" variant="ghost"
              onClick={downloadMissing} className="h-9 rounded-[6px] text-[13px]">
              <Download className="mr-1.5 h-3.5 w-3.5" /> {t("settings.data.integrity_download")}
            </Button>
          )}
        </div>
      </section>

      <section className="rounded-[8px] border border-state-critical-soft bg-state-critical-soft p-4">
        <div className="flex items-start gap-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-state-critical" />
          <div className="flex-1">
            <h2 className="text-[13px] font-semibold text-state-critical">{t("settings.data.reload_title")}</h2>
            <p className="mt-1 text-[12.5px] text-state-critical">{t("settings.data.reload_desc")}</p>

            {job && (isRunning || job.status === "fallido") ? (
              <div className="mt-3 max-w-md" data-testid="reload-progress-banner">
                {isRunning ? (
                  <>
                    <div className="mb-1.5 flex items-center justify-between text-[12px] text-state-critical">
                      <span className="inline-flex items-center gap-1.5" data-testid="reload-progress-stage">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {t(`settings.data.stages.${job.etapa}`)}
                      </span>
                      <span className="tabular-nums" data-testid="reload-progress-pct">{job.pct}%</span>
                    </div>
                    <Progress value={job.pct} className="h-1.5" />
                  </>
                ) : (
                  <div className="rounded-[6px] border border-state-critical-soft bg-white p-2.5 text-[12px] text-state-critical" data-testid="reload-failed-banner">
                    {t("settings.data.reload_failed")}: {job.error}
                  </div>
                )}
              </div>
            ) : null}

            <Button
              data-testid="open-reload-dialog-button"
              variant="destructive"
              className="mt-3 h-9 rounded-[6px] text-[13px]"
              disabled={isRunning}
              onClick={() => setConfirmOpen(true)}
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isRunning ? "animate-spin" : ""}`} />
              {isRunning ? t("settings.data.reloading") : t("settings.data.reload_button")}
            </Button>
          </div>
        </div>
      </section>

      <Dialog open={confirmOpen} onOpenChange={(v) => { setConfirmOpen(v); if (!v) setWord(""); }}>
        <DialogContent data-testid="reload-confirm-dialog" className="max-w-lg rounded-[10px]">
          <DialogHeader>
            <DialogTitle>{t("settings.data.reload_title")}</DialogTitle>
            <DialogDescription>{t("settings.data.reload_desc")}</DialogDescription>
          </DialogHeader>
          {s?.a_borrar && (
            <div className="rounded-[6px] border border-line bg-surface-sunken p-3 text-[12.5px]" data-testid="reload-preview-list">
              <p className="mb-1.5 font-medium text-ink">{t("settings.data.reload_will_delete")}</p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-ink-secondary">
                {Object.entries(s.a_borrar).map(([coll, n]) => (
                  <li key={coll} className="flex justify-between">
                    <span>{coll}</span><span className="tabular-nums font-medium">{n}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11.5px] text-ink-tertiary">{t("settings.data.reload_keeps")}</p>
            </div>
          )}
          <div className="mt-1">
            <label className="mb-1.5 block text-[12px] text-ink-tertiary">{t("settings.data.type_to_confirm")}</label>
            <Input
              data-testid="reload-confirm-input"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              placeholder="RECARGAR"
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-9 rounded-[6px] text-[13px]" onClick={() => setConfirmOpen(false)}>
              {t("app.cancel")}
            </Button>
            <Button
              data-testid="reload-confirm-button"
              variant="destructive"
              disabled={word.trim().toUpperCase() !== "RECARGAR" || reload.isPending}
              onClick={() => reload.mutate()}
              className="h-9 rounded-[6px] text-[13px]"
            >
              {reload.isPending ? t("settings.data.reloading") : t("settings.data.reload_button")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
